import { useMemo, useState } from "react";
import { Check, Store, ChevronRight, ArrowLeft } from "lucide-react";
import type { SetupStepProps } from "./types";
import type { BillingConfig } from "../../services/BillingProductsService";
import { getRootRetailer } from "../../utils/billingConfig";

interface BuildPlanStepProps extends SetupStepProps {
  selectedRetailers: string[];
  onPlanSelect: (retailer: string, planId: string) => void;
  billingConfig?: BillingConfig;
  canSelectPlan?: (retailer: string, planId: string) => boolean;
  isHolidaySaleEligible?: boolean;
}

interface RetailerGroup {
  name: string;
  displayName: string;
  plans: BillingConfig[string];
}

type SubStep = "retailers" | "walmart-config" | "homedepot-config";

export default function BuildPlanStep({
  selectedRetailers,
  onPlanSelect,
  onContinue,
  onBack,
  billingConfig,
  isHolidaySaleEligible,
  error,
}: BuildPlanStepProps) {
  const [subStep, setSubStep] = useState<SubStep>("retailers");

  // Group plans by retailer
  const retailerGroups = useMemo((): RetailerGroup[] => {
    if (!billingConfig) return [];
    const groups: { [key: string]: RetailerGroup } = {};
    
    for (const [retailer, plans] of Object.entries(billingConfig)) {
      const groupKey = getRootRetailer(retailer);
      const displayName = groupKey
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      
      if (!groups[groupKey]) {
        groups[groupKey] = { name: groupKey, displayName, plans: [] };
      }
      groups[groupKey].plans = [...groups[groupKey].plans, ...plans];
    }
    
    Object.values(groups).forEach((group) => {
      group.plans.sort((a, b) => a.storeLimit - b.storeLimit);
    });
    
    return Object.values(groups);
  }, [billingConfig]);

  const walmartGroup = retailerGroups.find((g) => g.name === "walmart");
  const homeDepotGroup = retailerGroups.find((g) => g.name === "home depot");

  const getSelectedPlanForGroup = (group: RetailerGroup) => {
    return group.plans.find((plan) => selectedRetailers.includes(plan.retailer || ""));
  };

  const hasWalmart = walmartGroup && getSelectedPlanForGroup(walmartGroup);
  const hasHomeDepot = homeDepotGroup && getSelectedPlanForGroup(homeDepotGroup);

  // Calculate total price
  const { totalPrice, breakdown } = useMemo(() => {
    let total = 25;
    const breakdown: { name: string; price: number; detail?: string }[] = [{ name: "Base App", price: 25 }];

    for (const retailer of selectedRetailers) {
      const plans = billingConfig?.[retailer];
      if (plans) {
        plans.forEach((plan) => {
          total += plan.price;
          const groupName = getRootRetailer(plan.retailer || "")
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          breakdown.push({ name: groupName, price: plan.price, detail: `${plan.storeLimit} locations` });
        });
      }
    }
    return { totalPrice: total, breakdown };
  }, [selectedRetailers, billingConfig]);

  const discountedTotal = isHolidaySaleEligible ? totalPrice * 0.5 : totalPrice;

  // Toggle retailer on/off (defaults to lowest tier)
  const handleRetailerToggle = (group: RetailerGroup) => {
    const selectedPlan = getSelectedPlanForGroup(group);
    if (selectedPlan) {
      onPlanSelect(selectedPlan.retailer || "", selectedPlan.displayName);
    } else {
      const defaultPlan = group.plans[0];
      if (defaultPlan) {
        onPlanSelect(defaultPlan.retailer || "", defaultPlan.displayName);
      }
    }
  };

  // Select specific tier
  const handleTierSelect = (group: RetailerGroup, planId: string) => {
    const currentPlan = getSelectedPlanForGroup(group);
    const newPlan = group.plans.find((p) => p.id === planId);
    if (!newPlan) return;
    
    if (currentPlan && currentPlan.id !== planId) {
      onPlanSelect(currentPlan.retailer || "", currentPlan.displayName);
    }
    if (!currentPlan || currentPlan.id !== planId) {
      onPlanSelect(newPlan.retailer || "", newPlan.displayName);
    }
  };

  // Navigation logic
  const handleSubStepNext = () => {
    if (subStep === "retailers") {
      if (hasWalmart) {
        setSubStep("walmart-config");
      } else if (hasHomeDepot) {
        setSubStep("homedepot-config");
      }
    } else if (subStep === "walmart-config") {
      if (hasHomeDepot) {
        setSubStep("homedepot-config");
      } else {
        onContinue();
      }
    } else if (subStep === "homedepot-config") {
      onContinue();
    }
  };

  const handleSubStepBack = () => {
    if (subStep === "retailers") {
      onBack?.();
    } else if (subStep === "walmart-config") {
      setSubStep("retailers");
    } else if (subStep === "homedepot-config") {
      if (hasWalmart) {
        setSubStep("walmart-config");
      } else {
        setSubStep("retailers");
      }
    }
  };

  // Render price summary
  const renderPriceSummary = () => (
    <div className="bg-gray-900 rounded-xl p-3 text-white shadow-lg">
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">Your Plan</p>
        {isHolidaySaleEligible && (
          <span className="bg-indigo-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">50% OFF</span>
        )}
      </div>
      <div className="space-y-1 mb-2 border-b border-gray-700 pb-2">
        {breakdown.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-xs">
            <span className="text-gray-300">
              {item.name}
              {item.detail && <span className="text-gray-500 ml-1">({item.detail})</span>}
            </span>
            <span className="text-gray-300">${item.price}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-300">Total</span>
        <div className="text-right">
          <p className="text-xl font-bold">${isHolidaySaleEligible ? discountedTotal : totalPrice}<span className="text-xs text-gray-400 font-normal">/mo</span></p>
          {isHolidaySaleEligible && <p className="text-[10px] text-gray-500 line-through">${totalPrice}/mo</p>}
        </div>
      </div>
    </div>
  );

  // Base Plan Header (always visible, not a choice)
  const renderBasePlanHeader = () => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white">
            <Check className="w-3 h-3" strokeWidth={3} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 text-sm">Base App</span>
              <span className="text-[9px] font-bold bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">REQUIRED</span>
            </div>
            <p className="text-[10px] text-gray-500">Discord, Alerts, Support, Search</p>
          </div>
        </div>
        <span className="font-bold text-gray-900">$25<span className="text-xs font-normal text-gray-500">/mo</span></span>
      </div>
    </div>
  );

  // Retailer Selection Step
  const renderRetailerStep = () => (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full mb-2">
          <span>Step 1 of 2</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Build Your Plan</h1>
        <p className="text-sm text-gray-500">Add retailers to track local deals</p>
      </div>

      {isHolidaySaleEligible && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">SALE</span>
          <span className="text-xs font-semibold text-indigo-900">50% off first month</span>
        </div>
      )}

      {/* Base Plan - Fixed at top, not a choice */}
      {renderBasePlanHeader()}

      {/* Retailer Add-ons */}
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Add Retailer Tracking</p>
        <div className="space-y-2">
          {retailerGroups.map((group) => {
            const isSelected = !!getSelectedPlanForGroup(group);
            const minPrice = Math.min(...group.plans.map((p) => p.price));

            return (
              <button
                key={group.name}
                onClick={() => handleRetailerToggle(group)}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? "border-purple-400 bg-purple-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 ${
                      isSelected ? "bg-purple-600 border-purple-600 text-white" : "bg-white border-gray-300"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={`text-sm font-semibold ${isSelected ? "text-purple-900" : "text-gray-800"}`}>
                      {group.displayName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {group.name === "walmart"
                        ? "Clearance & markdown deals"
                        : "Clearance & penny item deals"}
                    </p>
                    <p className={`text-xs mt-1 ${isSelected ? "text-purple-600 font-medium" : "text-gray-400"}`}>
                      Starting at ${minPrice}/mo for 1 location
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bonus retailers disclaimer */}
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5">
        <p className="text-xs text-emerald-800">
          <span className="font-semibold">Bonus:</span> Dollar General & Woot deals included with 5+ locations
        </p>
      </div>

      {selectedRetailers.length === 0 && (
        <p className="text-center text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
          Select at least one retailer to continue
        </p>
      )}

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {renderPriceSummary()}

      <div className="flex gap-3">
        <button
          onClick={handleSubStepBack}
          className="flex items-center gap-1 px-4 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSubStepNext}
          disabled={selectedRetailers.length === 0}
          className={`flex-1 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
            selectedRetailers.length > 0
              ? "bg-purple-600 hover:bg-purple-700 text-white"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Choose Coverage
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Store Tier Configuration Step
  const renderStoreTierStep = (group: RetailerGroup, stepLabel: string, isLast: boolean) => {
    const selectedPlan = getSelectedPlanForGroup(group);

    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full mb-2">
            <span>{stepLabel}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{group.displayName} Coverage</h1>
          <p className="text-sm text-gray-500">How many locations do you want to track?</p>
        </div>

        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
          <Store className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">More locations = More deals in your area</p>
        </div>

        <div className="space-y-2">
          {group.plans.map((plan, idx) => {
            const isSelectedTier = selectedPlan?.id === plan.id;

            return (
              <button
                key={plan.id}
                onClick={() => handleTierSelect(group, plan.id)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  isSelectedTier
                    ? "border-purple-400 bg-purple-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelectedTier ? "border-purple-600" : "border-gray-300"
                    }`}
                  >
                    {isSelectedTier && <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${isSelectedTier ? "text-purple-900" : "text-gray-800"}`}>
                          {plan.storeLimit} Location{plan.storeLimit > 1 ? "s" : ""}
                        </span>
                        {idx === 1 && (
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            POPULAR
                          </span>
                        )}
                      </div>
                      <span className={`text-lg font-bold ${isSelectedTier ? "text-purple-700" : "text-gray-700"}`}>
                        ${plan.price}<span className="text-sm font-normal text-gray-500">/mo</span>
                      </span>
                    </div>
                    {group.name === "home depot" && plan.storeLimit >= 5 && (
                      <p className="text-[10px] text-emerald-600 font-medium mt-0.5">+ Penny Items included</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {renderPriceSummary()}

        <div className="flex gap-3">
          <button
            onClick={handleSubStepBack}
            className="flex items-center gap-1 px-4 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handleSubStepNext}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            {isLast ? "Select Your Locations" : "Next: HD"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-[400px] mx-auto">
      {subStep === "retailers" && renderRetailerStep()}
      {subStep === "walmart-config" && walmartGroup && renderStoreTierStep(
        walmartGroup, 
        hasHomeDepot ? "Step 2a of 2" : "Step 2 of 2",
        !hasHomeDepot
      )}
      {subStep === "homedepot-config" && homeDepotGroup && renderStoreTierStep(
        homeDepotGroup, 
        hasWalmart ? "Step 2b of 2" : "Step 2 of 2",
        true
      )}
    </div>
  );
}
